/**
 * `facturas.estado_pago` — contrato de almacenamiento vs lectura UI.
 *
 * **Almacenado (DB, backfill, derive trigger, RPC):** solo tres estados derivables del saldo:
 *   `PAGADO` | `PENDIENTE_PAGO` | `PAGO_PARCIAL`
 *
 * **UI (lectura):** los tres anteriores + overlays que NO se persisten en `estado_pago`:
 *   - `VENCIDA` — saldo > 0 y `vencimiento` < hoy
 *   - `PENDIENTE_VERIFICACION` — `estado_verificacion`
 *   - `CANCELADO` — `facturas.estado` anulada/cancelada
 *
 * Backfill SQL, derive trigger y `crear_venta` deben escribir únicamente `EstadoPagoAlmacenado`.
 */

export const ESTADO_PAGO_ALMACENADO = ['PAGADO', 'PENDIENTE_PAGO', 'PAGO_PARCIAL'] as const;

export type EstadoPagoAlmacenado = (typeof ESTADO_PAGO_ALMACENADO)[number];

export type EstadoPagoUI =
  | EstadoPagoAlmacenado
  | 'VENCIDA'
  | 'PENDIENTE_VERIFICACION'
  | 'CANCELADO';

const PAGADO = new Set([
  'PAGADO',
  'PAGADA',
  'PAGADAS',
  'PAGADOS',
  'pagado',
  'pagada',
]);

const PENDIENTE = new Set([
  'PENDIENTE',
  'PENDIENTE_PAGO',
  'PENDIENTE DE PAGO',
  'pendiente',
  'pendiente_pago',
]);

const PARCIAL = new Set(['PAGO_PARCIAL', 'PARCIAL', 'parcial', 'pago_parcial']);

/** Legacy en columna — se mapean a almacenado; no re-persistir como VENCIDA/VERIFICACION. */
const VERIFICACION_LEGACY = new Set(['PENDIENTE_VERIFICACION', 'PENDIENTE VERIFICACION']);
const VENCIDA_LEGACY = new Set(['VENCIDA', 'VENCIDO', 'vencida', 'vencido']);
const CANCELADO_LEGACY = new Set(['CANCELADO', 'CANCELADA', 'cancelado', 'cancelada']);

function clean(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, '_');
}

/**
 * Normaliza un valor de columna (o legacy de cliente) al enum **almacenado** (3 valores).
 * Usar en store al mapear filas; backfill y trigger deben producir este mismo set.
 */
export function normalizeEstadoPagoAlmacenado(raw: unknown): EstadoPagoAlmacenado {
  const s = clean(raw);
  if (!s) return 'PENDIENTE_PAGO';
  if (PAGADO.has(s) || PAGADO.has(String(raw ?? '').trim())) return 'PAGADO';
  if (PARCIAL.has(s) || PARCIAL.has(String(raw ?? '').trim().toLowerCase())) return 'PAGO_PARCIAL';
  if (
    PENDIENTE.has(s) ||
    PENDIENTE.has(String(raw ?? '').trim().toLowerCase()) ||
    VERIFICACION_LEGACY.has(s) ||
    VENCIDA_LEGACY.has(s) ||
    CANCELADO_LEGACY.has(s)
  ) {
    return 'PENDIENTE_PAGO';
  }
  return 'PENDIENTE_PAGO';
}

/** @deprecated Alias — prefer `normalizeEstadoPagoAlmacenado` for clarity. */
export const normalizeEstadoPago = normalizeEstadoPagoAlmacenado;

/**
 * Deriva el valor a **persistir** desde montos (contrato del trigger / RPC).
 */
export function deriveEstadoPagoAlmacenadoFromSaldo(opts: {
  total?: unknown;
  monto_pagado?: unknown;
  montoPagado?: unknown;
  saldo_pendiente?: unknown;
  monto_pendiente?: unknown;
  montoPendiente?: unknown;
}): EstadoPagoAlmacenado {
  const total = Math.max(0, Number(opts.total) || 0);
  const pagado = numOrNaN(opts.monto_pagado ?? opts.montoPagado) || 0;
  const pendExplicit = [opts.saldo_pendiente, opts.monto_pendiente, opts.montoPendiente]
    .map(numOrNaN)
    .find((n) => !Number.isNaN(n));
  const saldo =
    pendExplicit != null && !Number.isNaN(pendExplicit)
      ? Math.max(0, pendExplicit)
      : Math.max(0, total - pagado);

  if (saldo <= 0 || (total > 0 && pagado >= total)) return 'PAGADO';
  if (pagado > 0) return 'PAGO_PARCIAL';
  return 'PENDIENTE_PAGO';
}

export type FilaEstadoPago = {
  estado_pago?: unknown;
  estadoPago?: unknown;
  estado?: unknown;
  vencimiento?: unknown;
  estado_verificacion?: unknown;
  estadoVerificacion?: unknown;
  total?: unknown;
  monto_pendiente?: unknown;
  montoPendiente?: unknown;
  saldo_pendiente?: unknown;
  monto_pagado?: unknown;
  montoPagado?: unknown;
};

function numOrNaN(v: unknown): number {
  if (v == null || v === '') return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

/** Saldo > 0 y fecha de vencimiento estrictamente anterior a hoy (local). */
export function facturaPagoVencida(f: FilaEstadoPago, hoy: Date = new Date()): boolean {
  if (facturaPagoCompletoDesdeFila(f)) return false;
  const raw = f.vencimiento;
  if (raw == null || raw === '') return false;
  const iso = typeof raw === 'string' ? raw.slice(0, 10) : null;
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const vd = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(vd.getTime())) return false;
  const h = new Date(hoy);
  h.setHours(0, 0, 0, 0);
  vd.setHours(0, 0, 0, 0);
  return vd < h;
}

/**
 * Estado para badges, filtros y copy — incluye overlays de lectura.
 */
export function estadoPagoParaUI(f: FilaEstadoPago, hoy?: Date): EstadoPagoUI {
  const est = String(f.estado ?? '').toUpperCase();
  if (est === 'ANULADA' || est === 'CANCELADO' || est === 'CANCELADA') return 'CANCELADO';

  const almacenado = normalizeEstadoPagoAlmacenado(f.estado_pago ?? f.estadoPago);
  if (almacenado === 'PAGADO') return 'PAGADO';

  const verif = String(f.estado_verificacion ?? f.estadoVerificacion ?? '').toUpperCase();
  if (verif === 'PENDIENTE_VERIFICACION') return 'PENDIENTE_VERIFICACION';

  if (facturaPagoVencida(f, hoy)) return 'VENCIDA';

  return almacenado;
}

export function estadoPagoEsPagado(rawOrFila: unknown): boolean {
  if (typeof rawOrFila === 'object' && rawOrFila != null) {
    return facturaPagoCompletoDesdeFila(rawOrFila as FilaEstadoPago);
  }
  return normalizeEstadoPagoAlmacenado(rawOrFila) === 'PAGADO';
}

export function estadoPagoEsParcial(rawOrFila: unknown): boolean {
  if (typeof rawOrFila === 'object' && rawOrFila != null) {
    return normalizeEstadoPagoAlmacenado(
      (rawOrFila as FilaEstadoPago).estado_pago ?? (rawOrFila as FilaEstadoPago).estadoPago,
    ) === 'PAGO_PARCIAL';
  }
  return normalizeEstadoPagoAlmacenado(rawOrFila) === 'PAGO_PARCIAL';
}

export function estadoPagoEsPendiente(rawOrFila: unknown): boolean {
  if (typeof rawOrFila === 'object' && rawOrFila != null) {
    return (
      normalizeEstadoPagoAlmacenado(
        (rawOrFila as FilaEstadoPago).estado_pago ?? (rawOrFila as FilaEstadoPago).estadoPago,
      ) === 'PENDIENTE_PAGO'
    );
  }
  return normalizeEstadoPagoAlmacenado(rawOrFila) === 'PENDIENTE_PAGO';
}

export function estadoPagoRequiereCobro(rawOrFila: unknown): boolean {
  if (typeof rawOrFila === 'object' && rawOrFila != null) {
    return !facturaPagoCompletoDesdeFila(rawOrFila as FilaEstadoPago);
  }
  const a = normalizeEstadoPagoAlmacenado(rawOrFila);
  return a === 'PENDIENTE_PAGO' || a === 'PAGO_PARCIAL';
}

/** Spanish label for badges / PDF (acepta fila completa o solo string almacenado). */
export function estadoPagoLabel(rawOrFila: unknown): string {
  const ui =
    typeof rawOrFila === 'object' && rawOrFila != null
      ? estadoPagoParaUI(rawOrFila as FilaEstadoPago)
      : estadoPagoParaUI({ estado_pago: rawOrFila });
  switch (ui) {
    case 'PAGADO':
      return 'Pagado';
    case 'PAGO_PARCIAL':
      return 'Pago parcial';
    case 'PENDIENTE_VERIFICACION':
      return 'Pendiente verificación';
    case 'CANCELADO':
      return 'Cancelado';
    case 'VENCIDA':
      return 'Vencida';
    default:
      return 'Pendiente de pago';
  }
}

/** Mobile Ventas list: 3-way bucket (vencida → pendiente). */
export function estadoPagoMobileBucket(rawOrFila: unknown): 'pagado' | 'parcial' | 'pendiente' {
  const ui =
    typeof rawOrFila === 'object' && rawOrFila != null
      ? estadoPagoParaUI(rawOrFila as FilaEstadoPago)
      : estadoPagoParaUI({ estado_pago: rawOrFila });
  if (ui === 'PAGADO') return 'pagado';
  if (ui === 'PAGO_PARCIAL') return 'parcial';
  return 'pendiente';
}

/**
 * Cobro completo: montos primero; `estado_pago` almacenado solo si no contradice saldo.
 */
export function facturaPagoCompletoDesdeFila(f: FilaEstadoPago): boolean {
  const tot = Number(f.total) || 0;
  const pend = [f.monto_pendiente, f.montoPendiente, f.saldo_pendiente]
    .map(numOrNaN)
    .find((n) => !Number.isNaN(n));
  const pag = numOrNaN(f.monto_pagado ?? f.montoPagado) || 0;

  if (tot > 0 && pend != null && !Number.isNaN(pend) && pend <= 0) return true;
  if (tot > 0 && pag >= tot) return true;

  if (normalizeEstadoPagoAlmacenado(f.estado_pago ?? f.estadoPago) === 'PAGADO') {
    if (tot > 0 && pend != null && !Number.isNaN(pend) && pend > 0) return false;
    if (tot > 0 && pag > 0 && pag < tot) return false;
    return true;
  }
  return false;
}

/** Etiqueta Ventas (PAGADA / PENDIENTE / ANULADA). */
export function estadoVentaLabelDesdeFactura(f: FilaEstadoPago): 'PAGADA' | 'PENDIENTE' | 'ANULADA' {
  if (String(f.estado ?? '').toUpperCase() === 'ANULADA') return 'ANULADA';
  return facturaPagoCompletoDesdeFila(f) ? 'PAGADA' : 'PENDIENTE';
}
